import { injectable } from 'tsyringe';
import type { BedrockModelInfo, BillingIntegrationConfig, BillingIntegrationUpdate, ModelSettings } from '@bmai/shared';
import { authenticatedFetch } from '@/shared/auth/AuthenticatedFetch';

const BASE = '/_billing/api/operator/billing/settings';

async function readError(res: Response, fallback: string): Promise<string> {
    try {
        const body = (await res.json()) as { error?: string };
        if (body.error) return body.error;
    } catch { /* keep fallback */ }
    return fallback;
}

/** Operator platform settings (Bedrock model per capability + payment-provider config) via the BFF. */
@injectable()
export class OperatorSettingsService {
    async getModels(): Promise<ModelSettings> {
        const res = await authenticatedFetch(`${BASE}/models`);
        if (!res.ok) throw new Error(await readError(res, `Failed to load model settings (${res.status})`));
        return (await res.json()) as ModelSettings;
    }

    /** Live Bedrock model catalog for the picker (from telemetry's nightly refresh). Empty → free-text only. */
    async getModelCatalog(): Promise<BedrockModelInfo[]> {
        try {
            const res = await authenticatedFetch('/_telemetry/api/operator/telemetry/models');
            if (!res.ok) return [];
            const body = await res.json();
            return Array.isArray(body) ? (body as BedrockModelInfo[]) : [];
        } catch {
            return [];
        }
    }

    async saveModels(models: ModelSettings): Promise<void> {
        const res = await authenticatedFetch(`${BASE}/models`, {
            method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(models),
        });
        if (!res.ok) throw new Error(await readError(res, `Failed to save model settings (${res.status})`));
    }

    async getBillingIntegration(): Promise<BillingIntegrationConfig> {
        const res = await authenticatedFetch(`${BASE}/billing-integration`);
        if (!res.ok) throw new Error(await readError(res, `Failed to load billing integration (${res.status})`));
        return (await res.json()) as BillingIntegrationConfig;
    }

    async saveBillingIntegration(update: BillingIntegrationUpdate): Promise<void> {
        const res = await authenticatedFetch(`${BASE}/billing-integration`, {
            method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(update),
        });
        if (!res.ok) throw new Error(await readError(res, `Failed to save billing integration (${res.status})`));
    }
}
